import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";
import { Modal } from "react-bootstrap";
import { FileText, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useModalDismiss } from "./useModalDismiss";
import ConfirmModal from "./ConfirmModal";

interface Category {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
}

interface Template {
    id: string;
    categoryId: string;
    title: string;
    body: string | null;
    status: string;
    updatedAt: string;
}

interface Props {
    categories: Category[];
    isAdmin: boolean;
    onSelectPage: (pageId: string, categoryId: string) => void;
    onPageCreated: (page: any) => void;
}

export default function TemplatesView({ categories, isAdmin, onSelectPage, onPageCreated }: Props) {
    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [preview, setPreview] = useState<Template | null>(null);
    const [useOpen, setUseOpen] = useState<Template | null>(null);
    const [newOpen, setNewOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);

    const categoriesById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);

    function load() {
        setLoading(true);
        axios.get("/api/manuals/templates")
            .then(res => {
                if (res.data.success) setTemplates(res.data.items);
                else toast.error(res.data.message || "Failed to load templates");
            })
            .catch(() => toast.error("Failed to load templates"))
            .finally(() => setLoading(false));
    }

    useEffect(() => { load(); }, []);

    function handleDeleteConfirmed(t: Template) {
        axios.delete(`/api/manuals/pages/${t.id}`)
            .then(res => {
                if (res.data.success) {
                    toast.success("Template deleted");
                    setTemplates(prev => prev.filter(x => x.id !== t.id));
                } else {
                    toast.error(res.data.message || "Failed to delete");
                }
            })
            .catch(() => toast.error("Failed to delete"))
            .finally(() => setDeleteTarget(null));
    }

    return (
        <div className="m-cat-page m-templates-page">
            <div className="m-cat-header">
                <div><h1 className="m-cat-title">Templates</h1></div>
            </div>

            <div className="m-templates-body">
                <div className="m-templates-toolbar">
                    <span className="m-templates-count">
                        {loading ? "Loading..." : `${templates.length} template${templates.length === 1 ? "" : "s"}`}
                    </span>
                    {isAdmin && (
                        <button className="m-btn-link" onClick={() => setNewOpen(true)}>
                            New template
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="m-loading"><CustomLoading /></div>
                ) : templates.length === 0 ? (
                    <div className="m-profile-empty">
                        <FileText size={32} color="#94a3b8" style={{ display: "block", margin: "0 auto 10px" }} />
                        <div>No templates yet.</div>
                        {isAdmin && (
                            <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 6 }}>
                                Click "New template" above, or open any page and use "Mark as template" in the kebab menu.
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="m-template-list">
                        {templates.map(t => (
                            <TemplateRow
                                key={t.id}
                                template={t}
                                isAdmin={isAdmin}
                                onPreview={() => setPreview(t)}
                                onUse={() => setUseOpen(t)}
                                onEdit={() => onSelectPage(t.id, t.categoryId)}
                                onDelete={() => setDeleteTarget(t)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {preview && (
                <PreviewModal
                    template={preview}
                    category={categoriesById.get(preview.categoryId) || null}
                    onClose={() => setPreview(null)}
                    onUse={() => { setUseOpen(preview); setPreview(null); }}
                    canUse={isAdmin}
                />
            )}

            {useOpen && (
                <UseTemplateModal
                    template={useOpen}
                    categories={categories}
                    onClose={() => setUseOpen(null)}
                    onCreated={(page) => {
                        setUseOpen(null);
                        onPageCreated(page);
                    }}
                />
            )}

            {newOpen && (
                <NewTemplateModal
                    categories={categories}
                    onClose={() => setNewOpen(false)}
                    onCreated={(page) => {
                        setNewOpen(false);
                        // stay on the templates list — do not navigate into the editor
                        setTemplates(prev => {
                            if (prev.some(t => t.id === page.id)) return prev;
                            return [{
                                id: page.id,
                                title: page.title,
                                categoryId: page.categoryId,
                                body: page.body ?? null,
                                status: page.status,
                                updatedAt: page.updatedAt,
                            } as Template, ...prev];
                        });
                    }}
                />
            )}

            {deleteTarget && (
                <ConfirmModal
                    title="Delete template"
                    message={`Delete "${deleteTarget.title}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => handleDeleteConfirmed(deleteTarget)}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}

interface RowProps {
    template: Template;
    isAdmin: boolean;
    onPreview: () => void;
    onUse: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

function TemplateRow({ template, isAdmin, onPreview, onUse, onEdit, onDelete }: RowProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuOpen) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
        };
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, [menuOpen]);

    return (
        <div className="m-template-row">
            <span className="m-template-row-icon"><FileText size={18} color="#14b8a6" /></span>
            <button className="m-template-row-title" onClick={onPreview}>{template.title}</button>
            <button className="m-template-row-create" onClick={onUse}>Create page</button>
            {isAdmin && (
                <div className={`m-template-row-menu-wrap${menuOpen ? " is-open" : ""}`} ref={ref}>
                    <button
                        className="m-cat-strip-kebab"
                        title="More actions"
                        onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
                    >
                        <MoreHorizontal size={16} />
                    </button>
                    {menuOpen && (
                        <div className="m-cat-kebab-menu">
                            <button onClick={() => { setMenuOpen(false); onEdit(); }}>
                                <Pencil size={14} /> Edit template
                            </button>
                            <button className="m-cat-kebab-danger" onClick={() => { setMenuOpen(false); onDelete(); }}>
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

interface PreviewProps {
    template: Template;
    category: Category | null;
    onClose: () => void;
    onUse: () => void;
    canUse: boolean;
}

function PreviewModal({ template, category, onClose, onUse, canUse }: PreviewProps) {
    const { show, dismiss, onExited } = useModalDismiss(onClose, onClose);
    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered size="lg" backdropClassName="m-modal-backdrop" scrollable>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>{template.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {category && (
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
                        Default category: <span style={{ color: category.color || "#1f2937" }}>{category.name}</span>
                    </div>
                )}
                {template.body ? (
                    <div className="m-page-body" dangerouslySetInnerHTML={{ __html: template.body }} />
                ) : (
                    <div className="m-page-body-empty">This template is empty.</div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Close</button>
                {canUse && (
                    <button type="button" className="m-btn m-btn-primary" onClick={onUse}>
                        Create page
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

interface UseProps {
    template: Template;
    categories: Category[];
    onClose: () => void;
    onCreated: (page: any) => void;
}

function UseTemplateModal({ template, categories, onClose, onCreated }: UseProps) {
    const [title, setTitle] = useState(template.title);
    const [categoryId, setCategoryId] = useState(template.categoryId);
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onClose, () => { /* onCreated fires directly */ });

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const t = title.trim();
        if (!t) { toast.error("Please enter a title"); return; }
        if (!categoryId) { toast.error("Please pick a category"); return; }
        setSaving(true);
        axios.post("/api/manuals/pages/from-template", {
            templateId: template.id,
            categoryId,
            folderId: null,
            title: t,
        })
            .then(res => {
                if (res.data.success) {
                    toast.success("Draft created from template");
                    onCreated(res.data.item);
                } else {
                    toast.error(res.data.message || "Failed to create page");
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to create page");
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Use template</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: "#64748b", fontSize: 13, marginTop: -4, marginBottom: 16 }}>
                        Creates a new draft page with the body of <strong>{template.title}</strong>.
                    </p>
                    <div className="mb-3">
                        <label className="m-label">Page title</label>
                        <input
                            type="text"
                            className="m-input"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            maxLength={512}
                            autoFocus
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Category</label>
                        <select
                            className="m-input"
                            value={categoryId}
                            onChange={e => setCategoryId(e.target.value)}
                        >
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
                        {saving ? "Creating..." : "Create draft"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

interface NewProps {
    categories: Category[];
    onClose: () => void;
    onCreated: (page: any) => void;
}

function NewTemplateModal({ categories, onClose, onCreated }: NewProps) {
    const [title, setTitle] = useState("");
    const [categoryId, setCategoryId] = useState(categories[0]?.id || "");
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onClose, () => { /* onCreated fires directly */ });

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const t = title.trim();
        if (!t) { toast.error("Please enter a title"); return; }
        if (!categoryId) { toast.error("Please pick a default category"); return; }
        setSaving(true);
        axios.post("/api/manuals/templates", {
            categoryId,
            title: t,
        })
            .then(res => {
                if (res.data.success) {
                    toast.success("Template draft created");
                    onCreated(res.data.item);
                } else {
                    toast.error(res.data.message || "Failed to create template");
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to create template");
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>New template</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: "#64748b", fontSize: 13, marginTop: -4, marginBottom: 16 }}>
                        Creates a blank template. Open it from the list to add content and publish when ready.
                    </p>
                    <div className="mb-3">
                        <label className="m-label">Template title</label>
                        <input
                            type="text"
                            className="m-input"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="e.g. Client Kickoff"
                            maxLength={512}
                            autoFocus
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Default category</label>
                        <select
                            className="m-input"
                            value={categoryId}
                            onChange={e => setCategoryId(e.target.value)}
                        >
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 4 }}>
                            Pages created from this template will default to this category (can be changed per use).
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
                        {saving ? "Creating..." : "Create template"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
